import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { AuthApiError, getAuthClient } from '@/api/client';
import type { MeResponse, OrgSummary } from '@/api/types';

interface AuthState {
  user: MeResponse | null;
  loading: boolean;
  error: string | null;

  bootstrap: () => Promise<void>;
  /** Phase 5.5b — 반환값으로 mfa challenge 여부 표시. challenge 면 user 미설정. */
  login: (
    identifier: string,
    password: string,
  ) => Promise<
    | { requiresMfa: false }
    | {
        requiresMfa: true;
        challengeId: string;
        method: string;
        recipientHint: string | null;
      }
  >;
  /** Phase 5.5b — challenge 응답으로 토큰 발급 + me. */
  loginMfa: (challengeId: string, code: string) => Promise<void>;
  signup: (
    input: {
      identifier: { email?: string; person_id?: string };
      password: string;
      name: string;
      phone?: string;
      organization_id?: string;
    },
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  switchOrg: (orgId: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      error: null,

      bootstrap: async () => {
        const client = getAuthClient();
        if (!client.isAuthenticated()) {
          set({ user: null, loading: false });
          return;
        }
        set({ loading: true });
        try {
          const me = await client.getMe();
          set({ user: me, error: null });
        } catch {
          set({ user: null });
        } finally {
          set({ loading: false });
        }
      },

      login: async (identifier, password) => {
        const client = getAuthClient();
        set({ loading: true, error: null });
        try {
          const res = await client.login(identifier, password);
          if ('mfa_required' in res && res.mfa_required) {
            // 토큰 미발급 — 호출자가 challenge 페이지로 이동 (method 별 안내 포함)
            return {
              requiresMfa: true,
              challengeId: res.challenge_id,
              method: res.method,
              recipientHint: res.recipient_hint,
            };
          }
          const me = await client.getMe();
          set({ user: me });
          return { requiresMfa: false };
        } catch (e) {
          if (e instanceof AuthApiError) set({ error: e.code });
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      loginMfa: async (challengeId, code) => {
        const client = getAuthClient();
        set({ loading: true, error: null });
        try {
          await client.loginMfa(challengeId, code);
          const me = await client.getMe();
          set({ user: me });
        } catch (e) {
          if (e instanceof AuthApiError) set({ error: e.code });
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      signup: async (input) => {
        const client = getAuthClient();
        set({ loading: true, error: null });
        try {
          await client.signup(input);
          const me = await client.getMe();
          set({ user: me });
        } catch (e) {
          if (e instanceof AuthApiError) set({ error: e.code });
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      logout: async () => {
        const client = getAuthClient();
        await client.logout();
        set({ user: null });
      },

      refreshMe: async () => {
        const client = getAuthClient();
        if (!client.isAuthenticated()) {
          set({ user: null });
          return;
        }
        try {
          const me = await client.getMe();
          set({ user: me });
        } catch {
          set({ user: null });
        }
      },

      switchOrg: (orgId) => {
        getAuthClient().setCurrentOrgId(orgId);
        const user = get().user;
        if (user) set({ user: { ...user, current_org_id: orgId } });
      },
    }),
    {
      name: 'ontoh.auth.store',
      // 토큰 자체는 AuthClient 가 관리. 여기는 user 객체만 캐시.
      partialize: (s) => ({ user: s.user }),
    },
  ),
);

export function selectIsAuthenticated(state: AuthState): boolean {
  return Boolean(state.user);
}

export function selectIsAttached(state: AuthState): boolean {
  return (state.user?.orgs.length ?? 0) > 0;
}

export function selectPrimaryOrg(state: AuthState): OrgSummary | null {
  return state.user?.orgs.find((o) => o.is_primary) ?? null;
}
